import { useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Upload, Trash2, ImageUp } from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";
import { useI18n } from "@/i18n";
import { Button } from "@/components/ui/button";
import { ProductThumb } from "@/components/common/ProductThumb";

const ACCEPT = ["image/jpeg", "image/png", "image/webp"];

export function ProductImageUploader({ productId, imageUrl, canManage, onChanged }) {
  const { t } = useI18n();
  const inputRef = useRef();
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    if (!ACCEPT.includes(file.type)) { toast.error(t("errors.unsupported_type")); return; }
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      await api.post(`/products/${productId}/image`, fd);
      toast.success(t("images.uploaded"));
      onChanged && onChanged();
    } catch (e) { toast.error(apiErrorMessage(e, t)); } finally { setBusy(false); }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/products/${productId}/image`);
      toast.success(t("images.removed"));
      onChanged && onChanged();
    } catch (e) { toast.error(apiErrorMessage(e, t)); } finally { setBusy(false); }
  };

  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center" data-testid="product-image-uploader">
      <ProductThumb path={imageUrl} size={96} />
      {canManage ? (
        <div className="flex-1">
          <div
            onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
            onDragLeave={() => setDrag(false)}
            onDrop={(e) => { e.preventDefault(); setDrag(false); upload(e.dataTransfer.files?.[0]); }}
            onClick={() => !busy && inputRef.current?.click()}
            data-testid="image-dropzone"
            className={`flex cursor-pointer flex-col items-center justify-center rounded-md border-2 border-dashed px-4 py-4 text-center text-sm transition-colors ${drag ? "border-primary bg-primary/5" : "border-slate-300 hover:border-slate-400 dark:border-slate-600"}`}
          >
            {busy ? <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
                  : <ImageUp className="h-5 w-5 text-slate-400" />}
            <span className="mt-1 text-slate-500">{t("images.drop_here")}</span>
            <span className="text-xs text-slate-400">{t("images.formats_hint")}</span>
          </div>
          <input ref={inputRef} type="file" accept={ACCEPT.join(",")} className="hidden"
                 data-testid="image-file-input"
                 onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} />
          <div className="mt-2 flex gap-2">
            <Button type="button" variant="outline" size="sm" disabled={busy}
                    onClick={() => inputRef.current?.click()} data-testid="image-upload-button">
              <Upload className="h-4 w-4" />{imageUrl ? t("images.replace") : t("images.upload")}
            </Button>
            {imageUrl && (
              <Button type="button" variant="ghost" size="sm" disabled={busy} onClick={remove}
                      data-testid="image-remove-button">
                <Trash2 className="h-4 w-4 text-red-500" />{t("images.remove")}
              </Button>
            )}
          </div>
        </div>
      ) : (
        <p className="text-sm text-slate-400">{imageUrl ? "" : t("images.none")}</p>
      )}
    </div>
  );
}
